import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AdminNotificationKind } from "@mnp/shared";
import { api } from "./api";

// Единый источник — @mnp/shared (волна 6, admin-hook-type-mirrors). Ре-экспорт
// сохраняет прежний путь импорта для потребителей этого хука.
export type { AdminNotificationKind };

export interface AdminNotification {
  id: string;
  kind: AdminNotificationKind;
  icon: string;
  title: string;
  body: string;
  createdAt: string;
  unread: boolean;
}

export interface AdminNotificationCount {
  unread: number;
}

export const ADMIN_NOTIFICATIONS_QUERY_KEY = ["admin-notifications"] as const;
export const ADMIN_NOTIFICATION_COUNT_QUERY_KEY = ["admin-notification-count"] as const;

export function useAdminNotifications() {
  return useQuery({
    queryKey: ADMIN_NOTIFICATIONS_QUERY_KEY,
    queryFn: () => api<AdminNotification[]>("/admin/notifications"),
    refetchInterval: 30_000,
  });
}

export function useAdminNotificationCount() {
  return useQuery({
    queryKey: ADMIN_NOTIFICATION_COUNT_QUERY_KEY,
    queryFn: () => api<AdminNotificationCount>("/admin/notifications/count"),
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationsSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api<{ ok: true }>("/admin/notifications/mark-seen", { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_NOTIFICATION_COUNT_QUERY_KEY });
    },
  });
}

// Per-notification "seen" (fired on hover). Optimistically flips that item
// red→yellow (unread=false) and decrements the bell count so the glow fades
// without waiting for a refetch.
export function useMarkNotificationSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: true }>(`/admin/notifications/${encodeURIComponent(id)}/seen`, { method: "POST" }),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ADMIN_NOTIFICATIONS_QUERY_KEY });
      const prevList = queryClient.getQueryData<AdminNotification[]>(ADMIN_NOTIFICATIONS_QUERY_KEY);
      const prevCount = queryClient.getQueryData<AdminNotificationCount>(
        ADMIN_NOTIFICATION_COUNT_QUERY_KEY,
      );
      const target = prevList?.find((n) => n.id === id);
      if (prevList && target?.unread) {
        queryClient.setQueryData<AdminNotification[]>(
          ADMIN_NOTIFICATIONS_QUERY_KEY,
          prevList.map((n) => (n.id === id ? { ...n, unread: false } : n)),
        );
        if (prevCount) {
          queryClient.setQueryData<AdminNotificationCount>(ADMIN_NOTIFICATION_COUNT_QUERY_KEY, {
            unread: Math.max(0, prevCount.unread - 1),
          });
        }
      }
      return { prevList, prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevList) {
        queryClient.setQueryData(ADMIN_NOTIFICATIONS_QUERY_KEY, ctx.prevList);
      }
      if (ctx?.prevCount) {
        queryClient.setQueryData(ADMIN_NOTIFICATION_COUNT_QUERY_KEY, ctx.prevCount);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_NOTIFICATION_COUNT_QUERY_KEY });
    },
  });
}
